import { Card, Col, Row, Table, Tabs, Typography } from "antd";
import type { ColumnType } from "antd/lib/table";
import type { SorterResult } from "antd/lib/table/interface";
import { ImpressaoContainer, ImpressaoHeader } from "components/Impressao";
import type {
	ApiRelAnaliseQualidadeMedias,
	ApiRelAnaliseQualidadeProdutor,
	ApiRelAnaliseQualidadeResumo,
	ApiRelAnaliseQualidadeRota,
} from "pages/relatorios/types/ApiRelAnaliseQualidade";
import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type TdHTMLAttributes } from "react";
import { decimalFormater } from "std/format";
import type { PrintOptionsAnaliseQualidade } from "./printOptions";

const PRINT_PAGE_STYLE = `
@page { size: A4 landscape; margin: 4mm 5mm; }
.rel-aq-table { margin-top: 0 !important; }
.rel-aq-screen, .rel-aq-help, .rel-aq-warning, .ant-pagination, .ant-table-column-sorter { display: none !important; }
.rel-aq-print { display: block !important; }
.print-only .ant-image, .print-only img { width: 36px !important; height: 36px !important; }
.print-only h1, .print-only h2.ant-typography, .print-only .ant-typography { margin: 0 !important; }
.print-only h2 { font-size: 13px !important; line-height: 1.2 !important; }
.print-only h5 { font-size: 10px !important; line-height: 1.2 !important; }
.print-only .ant-row { margin-bottom: 2px !important; }
.ant-table-content, .ant-table-body, .ant-table-container { overflow: visible !important; overflow-x: visible !important; }
.ant-table { width: 100% !important; font-size: 8px !important; }
.ant-table table { width: 100% !important; table-layout: fixed !important; }
.ant-table-thead > tr > th, .ant-table-tbody > tr > td { padding: 1px 3px !important; }
.ant-table-tbody > tr > td { white-space: normal; word-break: break-word; }
.rel-aq-print .ant-table-row-expand-icon { display: none !important; }
.rel-aq-print-rotas { width: 100%; }
.rel-aq-print-rota-block {
	margin-bottom: 6px;
	page-break-inside: avoid;
	break-inside: avoid;
}
.rel-aq-print-rota-block .ant-table { margin-bottom: 0 !important; }
.rel-aq-print .ant-table table {
	table-layout: auto !important;
	width: max-content !important;
	min-width: 100%;
}
.rel-aq-print .ant-table-thead > tr > th { white-space: nowrap; }
`;

const TABLE_STYLE = `
.rel-aq-print { display: none; }
.rel-aq-table .ant-table { font-size: 12px; width: 100% !important; }
.rel-aq-table .ant-table table {
	width: 100% !important;
	min-width: 0 !important;
	table-layout: fixed !important;
}
.rel-aq-table .ant-table-thead > tr > th {
	padding: 3px 4px !important;
	white-space: normal !important;
	word-break: break-word;
	overflow-wrap: anywhere;
	min-width: 0 !important;
}
.rel-aq-table .ant-table-tbody > tr > td {
	padding: 3px 4px !important;
	white-space: normal !important;
	word-break: break-word;
	overflow-wrap: anywhere;
	overflow: visible !important;
	text-overflow: unset !important;
	min-width: 0 !important;
}
.rel-aq-table .ant-table-expanded-row .ant-table-wrapper { margin: 0; }
.rel-aq-tab-produtores .ant-table-container,
.rel-aq-tab-produtores .ant-table-content,
.rel-aq-tab-produtores .ant-table-body,
.rel-aq-tab-produtores .ant-table-wrapper {
	overflow-x: hidden !important;
	max-width: 100%;
}
.rel-aq-tab-rotas > .ant-table-wrapper .ant-table-container,
.rel-aq-tab-rotas > .ant-table-wrapper .ant-table-content,
.rel-aq-tab-rotas > .ant-table-wrapper .ant-table-body {
	overflow-x: auto !important;
	max-width: 100%;
}
.rel-aq-tab-rotas .ant-table-expanded-row > .ant-table-cell {
	overflow: visible !important;
	padding: 6px 8px !important;
	background: #fafafa;
}
.rel-aq-tab-rotas .ant-table-expanded-row .ant-table-container,
.rel-aq-tab-rotas .ant-table-expanded-row .ant-table-content,
.rel-aq-tab-rotas .ant-table-expanded-row .ant-table-body {
	overflow-x: auto !important;
}
.rel-aq-nested-table {
	margin: 0;
	max-width: 100%;
	padding-right: 4px;
	box-sizing: border-box;
}
.rel-aq-nested-table .ant-table {
	min-width: 100%;
}
.rel-aq-nested-table .ant-table-content {
	padding-right: 10px;
	box-sizing: border-box;
}
.rel-aq-nested-table .ant-table-thead > tr > th,
.rel-aq-nested-table .ant-table-tbody > tr > td {
	overflow: visible !important;
	text-overflow: unset !important;
}
.rel-aq-nested-table .ant-table-tbody > tr > td:not(.rel-aq-nome) {
	white-space: nowrap !important;
}
.rel-aq-nested-table .ant-table-thead > tr > th:last-child,
.rel-aq-nested-table .ant-table-tbody > tr > td:last-child {
	min-width: 68px !important;
	padding-right: 8px !important;
}
.rel-aq-resize-handle {
	position: absolute;
	right: 0;
	top: 0;
	width: 8px;
	height: 100%;
	cursor: col-resize;
	z-index: 2;
	user-select: none;
}
.rel-aq-resize-handle:hover,
.rel-aq-resize-handle.is-active {
	background: rgba(22, 119, 255, 0.35);
}
`;

const { Title, Text } = Typography;

type QualidadeKey = keyof ApiRelAnaliseQualidadeMedias;

const QUALIDADE_COLS: Array<{ key: QualidadeKey; title: string; scale: number; width: number }> = [
	{ key: "gordura", title: "Gor", scale: 2, width: 52 },
	{ key: "proteina", title: "Prot", scale: 2, width: 52 },
	{ key: "lactose", title: "Lact", scale: 2, width: 52 },
	{ key: "st", title: "ST", scale: 2, width: 48 },
	{ key: "esd", title: "ESD", scale: 2, width: 48 },
	{ key: "ureia", title: "Ure", scale: 2, width: 48 },
	{ key: "caseina", title: "Cas", scale: 2, width: 48 },
	{ key: "ccs", title: "CCS", scale: 0, width: 64 },
	{ key: "cbt", title: "CBT", scale: 0, width: 68 },
];

function formatQualidade(value: number | null | undefined, scale: number): string {
	if (value == null || Number.isNaN(Number(value))) {
		return "—";
	}

	return decimalFormater(value, scale);
}

function toSortNumber(value: unknown): number | null {
	if (value == null || value === "") {
		return null;
	}

	if (typeof value === "number") {
		return Number.isFinite(value) ? value : null;
	}

	let text = String(value).trim();
	if (text.includes(",")) {
		text = text.replace(/\./g, "").replace(",", ".");
	}

	const parsed = Number(text);
	return Number.isFinite(parsed) ? parsed : null;
}

type ColumnSort = {
	key: string;
	order: "ascend" | "descend";
};

function readColumnSort<T>(sorter: SorterResult<T> | SorterResult<T>[]): ColumnSort | null {
	const current = Array.isArray(sorter) ? sorter[0] : sorter;
	if (!current?.order) {
		return null;
	}

	const key = current.columnKey ?? current.field;
	if (key == null) {
		return null;
	}

	return { key: String(key), order: current.order };
}

function withSortOrder<T>(columns: ColumnType<T>[], sort: ColumnSort | null): ColumnType<T>[] {
	return columns.map((col) => ({
		...col,
		sortOrder: sort && String(col.key ?? col.dataIndex) === sort.key ? sort.order : null,
	}));
}

function withoutSorters<T>(columns: ColumnType<T>[]): ColumnType<T>[] {
	return columns.map(({ sorter: _sorter, sortOrder: _sortOrder, ...col }) => col);
}

function sortByColumn<T>(data: T[], columns: ColumnType<T>[], sort: ColumnSort | null): T[] {
	if (!sort) {
		return data;
	}

	const column = columns.find((col) => String(col.key ?? col.dataIndex) === sort.key);
	const sorter = column?.sorter;
	const compare = typeof sorter === "function" ? sorter : sorter && typeof sorter === "object" ? sorter.compare : null;

	if (!compare) {
		return data;
	}

	return [...data].sort((a, b) => {
		const result = compare(a, b, sort.order);
		return sort.order === "descend" ? -result : result;
	});
}

function compareNumber(a: unknown, b: unknown, sortOrder?: "ascend" | "descend" | null): number {
	const aNum = toSortNumber(a);
	const bNum = toSortNumber(b);
	const aNull = aNum == null;
	const bNull = bNum == null;

	if (aNull && bNull) {
		return 0;
	}

	// Ant Design inverte o resultado no descend — compensar pra "—" ficar sempre no fim.
	if (aNull) {
		return sortOrder === "descend" ? -1 : 1;
	}

	if (bNull) {
		return sortOrder === "descend" ? 1 : -1;
	}

	return aNum - bNum;
}

function qualidadeColumns<T extends ApiRelAnaliseQualidadeMedias>(): ColumnType<T>[] {
	return QUALIDADE_COLS.map((col) => ({
		dataIndex: col.key,
		key: col.key,
		title: col.title,
		align: "right" as const,
		width: col.width,
		sorter: (a: T, b: T, sortOrder) => compareNumber(a[col.key], b[col.key], sortOrder),
		render: (value: number | null) => formatQualidade(value, col.scale),
	}));
}

function ResumoCards({ resumo }: { resumo?: ApiRelAnaliseQualidadeResumo }) {
	if (!resumo) {
		return null;
	}

	const items = [
		{ label: "Rotas", value: decimalFormater(resumo.qtdRotas, 0) },
		{ label: "Produtores", value: decimalFormater(resumo.qtdProdutores, 0) },
		{ label: "Análises", value: decimalFormater(resumo.qtdAnalises, 0) },
		{ label: "Litragem", value: decimalFormater(resumo.litros, 1) },
		{ label: "Gordura (kg)", value: decimalFormater(resumo.gorduraKg, 2) },
		{ label: "Proteína (kg)", value: decimalFormater(resumo.proteinaKg, 2) },
		{ label: "Gordura méd.", value: formatQualidade(resumo.gordura, 2) },
		{ label: "Proteína méd.", value: formatQualidade(resumo.proteina, 2) },
		{ label: "CCS méd.", value: formatQualidade(resumo.ccs, 0) },
		{ label: "CBT méd.", value: formatQualidade(resumo.cbt, 0) },
		{ label: "ESD méd.", value: formatQualidade(resumo.esd, 2) },
		{ label: "ST méd.", value: formatQualidade(resumo.st, 2) },
	];

	return (
		<Row gutter={[10, 10]} className="mb-3 rel-aq-resumo">
			{items.map((item) => (
				<Col key={item.label} xs={12} sm={8} md={6} lg={4} xl={4}>
					<Card size="small">
						<Text type="secondary">{item.label}</Text>
						<Title level={4} style={{ margin: 0 }}>
							{item.value}
						</Title>
					</Card>
				</Col>
			))}
		</Row>
	);
}

const rotaColumns: ColumnType<ApiRelAnaliseQualidadeRota>[] = [
	{
		dataIndex: "rotaIdentificacao",
		key: "rotaIdentificacao",
		title: "Rota",
		width: 90,
		sorter: (a, b) => a.rotaIdentificacao.localeCompare(b.rotaIdentificacao),
	},
	{
		dataIndex: "qtdProdutores",
		key: "qtdProdutores",
		title: "Prod.",
		align: "right",
		width: 56,
		sorter: (a, b, sortOrder) => compareNumber(a.qtdProdutores, b.qtdProdutores, sortOrder),
	},
	{
		dataIndex: "litros",
		key: "litros",
		title: "L",
		align: "right",
		width: 72,
		sorter: (a, b, sortOrder) => compareNumber(a.litros, b.litros, sortOrder),
		render: (value: number) => decimalFormater(value || 0, 1),
	},
	...qualidadeColumns<ApiRelAnaliseQualidadeRota>(),
	{
		dataIndex: "gorduraKg",
		key: "gorduraKg",
		title: "Gor kg",
		align: "right",
		width: 64,
		sorter: (a, b, sortOrder) => compareNumber(a.gorduraKg, b.gorduraKg, sortOrder),
		render: (value: number) => decimalFormater(value || 0, 2),
	},
	{
		dataIndex: "proteinaKg",
		key: "proteinaKg",
		title: "Prot kg",
		align: "right",
		width: 64,
		sorter: (a, b, sortOrder) => compareNumber(a.proteinaKg, b.proteinaKg, sortOrder),
		render: (value: number) => decimalFormater(value || 0, 2),
	},
];

export const produtorColumns: ColumnType<ApiRelAnaliseQualidadeProdutor>[] = [
	{
		dataIndex: "produtorCodigo",
		key: "produtorCodigo",
		title: "Cód.",
		align: "center",
		width: 64,
		sorter: (a, b) =>
			String(a.produtorCodigo ?? "").localeCompare(String(b.produtorCodigo ?? ""), "pt-BR", {
				numeric: true,
				sensitivity: "base",
			}),
	},
	{
		dataIndex: "produtorNome",
		key: "produtorNome",
		title: "Produtor",
		width: 160,
		className: "rel-aq-nome",
		sorter: (a, b) => a.produtorNome.localeCompare(b.produtorNome),
	},
	{
		dataIndex: "rotaIdentificacao",
		key: "rotaIdentificacao",
		title: "Rota",
		width: 80,
		sorter: (a, b) => a.rotaIdentificacao.localeCompare(b.rotaIdentificacao),
	},
	{
		dataIndex: "litros",
		key: "litros",
		title: "L",
		align: "right",
		width: 72,
		sorter: (a, b, sortOrder) => compareNumber(a.litros, b.litros, sortOrder),
		render: (value: number) => decimalFormater(value || 0, 1),
	},
	...qualidadeColumns<ApiRelAnaliseQualidadeProdutor>(),
];

const nestedProdutorColumns = produtorColumns.filter((col) => col.key !== "rotaIdentificacao");

function rotaRowKey(row: ApiRelAnaliseQualidadeRota): string {
	return String(row.rotaId ?? row.rotaIdentificacao);
}

const COL_WIDTHS_STORAGE_KEY = "lacto:analise-qualidade:col-widths";

const DEFAULT_COL_WIDTHS: Record<string, number> = {
	produtorCodigo: 64,
	produtorNome: 160,
	rotaIdentificacao: 90,
	qtdProdutores: 56,
	litros: 72,
	gordura: 56,
	proteina: 56,
	lactose: 56,
	st: 52,
	esd: 52,
	ureia: 52,
	caseina: 52,
	ccs: 64,
	cbt: 68,
	gorduraKg: 64,
	proteinaKg: 64,
};

type ResizeCellProps = TdHTMLAttributes<HTMLTableCellElement> & {
	width?: number;
	onResize?: (width: number) => void;
};

function ResizableTitle({ width, onResize, children, style, ...rest }: ResizeCellProps) {
	const dragging = useRef(false);
	const startX = useRef(0);
	const startWidth = useRef(0);

	if (!width || !onResize) {
		return (
			<th {...rest} style={style}>
				{children}
			</th>
		);
	}

	const handleMouseDown = (event: ReactMouseEvent<HTMLSpanElement>) => {
		event.preventDefault();
		event.stopPropagation();
		dragging.current = true;
		startX.current = event.clientX;
		startWidth.current = width;
		event.currentTarget.classList.add("is-active");
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";

		const handle = event.currentTarget;

		const onMove = (moveEvent: globalThis.MouseEvent) => {
			if (!dragging.current) {
				return;
			}

			onResize(Math.min(480, Math.max(48, startWidth.current + (moveEvent.clientX - startX.current))));
		};

		const onUp = () => {
			dragging.current = false;
			handle.classList.remove("is-active");
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
			window.removeEventListener("mousemove", onMove);
			window.removeEventListener("mouseup", onUp);
		};

		window.addEventListener("mousemove", onMove);
		window.addEventListener("mouseup", onUp);
	};

	return (
		<th {...rest} style={{ ...style, width, position: "relative" }}>
			{children}
			<span className="rel-aq-resize-handle" onMouseDown={handleMouseDown} />
		</th>
	);
}

function loadColWidths(): Record<string, number> {
	try {
		const raw = window.localStorage.getItem(COL_WIDTHS_STORAGE_KEY);
		if (!raw) {
			return { ...DEFAULT_COL_WIDTHS };
		}

		return { ...DEFAULT_COL_WIDTHS, ...(JSON.parse(raw) as Record<string, number>) };
	} catch {
		return { ...DEFAULT_COL_WIDTHS };
	}
}

function columnsScrollX<T>(columns: ColumnType<T>[], extra = 48): number {
	return columns.reduce((acc, col) => acc + Number(col.width || 0), 0) + extra;
}

function buildRotasDataSource(
	rotas: ApiRelAnaliseQualidadeRota[],
	rotaSort: ColumnSort | null,
	produtorSort: ColumnSort | null,
): ApiRelAnaliseQualidadeRota[] {
	let list = rotaSort ? sortByColumn(rotas, rotaColumns, rotaSort) : rotas;

	if (!produtorSort) {
		return list;
	}

	return list.map((rota) => ({
		...rota,
		produtores: sortByColumn(rota.produtores, nestedProdutorColumns, produtorSort),
	}));
}

function withColumnResize<T>(
	columns: ColumnType<T>[],
	widths: Record<string, number>,
	onResize: (key: string, width: number) => void,
	enabled: boolean,
): ColumnType<T>[] {
	return columns.map((col) => {
		const key = String(col.key ?? col.dataIndex);
		const width = widths[key] ?? Number(col.width) ?? 80;

		if (!enabled) {
			return { ...col, width };
		}

		return {
			...col,
			width,
			onHeaderCell: () =>
				({
					width,
					onResize: (next: number) => onResize(key, next),
				}) as TdHTMLAttributes<HTMLTableCellElement>,
		};
	});
}

function TabelaRotas({
	rotas,
	expandedKeys,
	onExpandedRowsChange,
	showProdutores,
	forPrint = false,
	widths,
	onResizeWidth,
	rotaSort,
	produtorSort,
	onRotaSortChange,
	onProdutorSortChange,
}: {
	rotas: ApiRelAnaliseQualidadeRota[];
	expandedKeys: string[];
	onExpandedRowsChange?: (keys: string[]) => void;
	showProdutores: boolean;
	forPrint?: boolean;
	widths: Record<string, number>;
	onResizeWidth: (key: string, width: number) => void;
	rotaSort: ColumnSort | null;
	produtorSort: ColumnSort | null;
	onRotaSortChange: (sort: ColumnSort | null) => void;
	onProdutorSortChange: (sort: ColumnSort | null) => void;
}) {
	const columns = useMemo(() => {
		const next = withSortOrder(withColumnResize(rotaColumns, widths, onResizeWidth, !forPrint), rotaSort);
		return forPrint ? withoutSorters(next) : next;
	}, [forPrint, onResizeWidth, rotaSort, widths]);
	const nestedColumns = useMemo(() => {
		const next = withSortOrder(withColumnResize(nestedProdutorColumns, widths, onResizeWidth, !forPrint), produtorSort);
		return forPrint ? withoutSorters(next) : next;
	}, [forPrint, onResizeWidth, produtorSort, widths]);
	const dataSource = useMemo(
		() => buildRotasDataSource(rotas, rotaSort, produtorSort),
		[produtorSort, rotaSort, rotas],
	);

	const rotaScrollX = useMemo(() => columnsScrollX(columns), [columns]);
	const nestedScrollX = useMemo(() => columnsScrollX(nestedColumns, 72), [nestedColumns]);

	if (forPrint && showProdutores) {
		return (
			<div className="rel-aq-print-rotas">
				{dataSource.map((rota) => (
					<section key={rotaRowKey(rota)} className="rel-aq-print-rota-block">
						<Table<ApiRelAnaliseQualidadeRota>
							rowKey={rotaRowKey}
							columns={columns}
							dataSource={[rota]}
							pagination={false}
							size="small"
							showSorterTooltip={false}
							tableLayout="fixed"
							style={{ width: "100%" }}
						/>
						<Table<ApiRelAnaliseQualidadeProdutor>
							rowKey={(row) => `${row.produtorId}-${row.rotaId || "rota"}`}
							bordered={true}
							columns={nestedColumns}
							dataSource={rota.produtores}
							pagination={false}
							size="small"
							tableLayout="fixed"
							style={{ width: "100%" }}
							summary={() => (
								<Table.Summary.Row>
									<Table.Summary.Cell index={0} colSpan={3}>
										<Title level={5} style={{ margin: 0 }}>
											Produtores: {rota.qtdProdutores} — Litros: {decimalFormater(rota.litros, 1)}
										</Title>
									</Table.Summary.Cell>
								</Table.Summary.Row>
							)}
						/>
					</section>
				))}
			</div>
		);
	}

	return (
		<div className={forPrint ? undefined : "rel-aq-tab-rotas"}>
			<Table<ApiRelAnaliseQualidadeRota>
				rowKey={rotaRowKey}
				columns={columns}
				dataSource={dataSource}
				pagination={false}
				size="small"
				rowClassName="zebra"
				showSorterTooltip={false}
				sortDirections={["ascend", "descend", "ascend"]}
				tableLayout="fixed"
				style={{ width: "100%" }}
				scroll={forPrint ? undefined : { x: rotaScrollX }}
				components={forPrint ? undefined : { header: { cell: ResizableTitle } }}
				onChange={(_, __, sorter) => onRotaSortChange(readColumnSort(sorter))}
				expandable={
					showProdutores
						? {
								expandedRowKeys: expandedKeys,
								onExpandedRowsChange: (keys) => onExpandedRowsChange?.(keys.map(String)),
								expandedRowRender: (record) => (
									<div className="rel-aq-nested-table">
										<Table<ApiRelAnaliseQualidadeProdutor>
											rowKey={(row) => `${row.produtorId}-${row.rotaId || "rota"}`}
											bordered={true}
											columns={nestedColumns}
											dataSource={record.produtores}
											pagination={false}
											size="small"
											showSorterTooltip={false}
											sortDirections={["ascend", "descend", "ascend"]}
											tableLayout="fixed"
											scroll={forPrint ? undefined : { x: nestedScrollX }}
											components={forPrint ? undefined : { header: { cell: ResizableTitle } }}
											onChange={(_, __, sorter) => onProdutorSortChange(readColumnSort(sorter))}
											summary={() => (
												<Table.Summary.Row>
													<Table.Summary.Cell index={0} colSpan={3}>
														<Title level={5} style={{ margin: 0 }}>
															Produtores: {record.qtdProdutores} — Litros:{" "}
															{decimalFormater(record.litros, 1)}
														</Title>
													</Table.Summary.Cell>
												</Table.Summary.Row>
											)}
										/>
									</div>
								),
							}
						: undefined
				}
			/>
		</div>
	);
}

function TabelaProdutores({
	produtores,
	forPrint = false,
	widths,
	onResizeWidth,
	sort,
	onSortChange,
}: {
	produtores: ApiRelAnaliseQualidadeProdutor[];
	forPrint?: boolean;
	widths: Record<string, number>;
	onResizeWidth: (key: string, width: number) => void;
	sort: ColumnSort | null;
	onSortChange: (sort: ColumnSort | null) => void;
}) {
	const columns = useMemo(() => {
		const next = withSortOrder(withColumnResize(produtorColumns, widths, onResizeWidth, !forPrint), sort);
		return forPrint ? withoutSorters(next) : next;
	}, [forPrint, onResizeWidth, sort, widths]);
	const dataSource = useMemo(
		() => (forPrint ? sortByColumn(produtores, produtorColumns, sort) : produtores),
		[forPrint, produtores, sort],
	);

	return (
		<Table<ApiRelAnaliseQualidadeProdutor>
			rowKey={(row) => `${row.produtorId}-${row.rotaId || "rota"}`}
			columns={columns}
			dataSource={dataSource}
			pagination={false}
			size="small"
			rowClassName="zebra"
			showSorterTooltip={false}
			sortDirections={["ascend", "descend", "ascend"]}
			tableLayout="fixed"
			style={{ width: "100%" }}
			components={forPrint ? undefined : { header: { cell: ResizableTitle } }}
			onChange={(_, __, sorter) => onSortChange(readColumnSort(sorter))}
		/>
	);
}

export function TableRelAnaliseQualidade({
	rotas,
	produtores,
	resumo,
	title,
	activeTab,
	onTabChange,
	printOptions,
}: {
	rotas: ApiRelAnaliseQualidadeRota[];
	produtores: ApiRelAnaliseQualidadeProdutor[];
	resumo?: ApiRelAnaliseQualidadeResumo;
	title: string;
	activeTab: string;
	onTabChange: (tab: string) => void;
	printOptions: PrintOptionsAnaliseQualidade;
}) {
	const allRotaKeys = useMemo(() => rotas.map(rotaRowKey), [rotas]);
	const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
	const [colWidths, setColWidths] = useState<Record<string, number>>(loadColWidths);
	const [produtorSort, setProdutorSort] = useState<ColumnSort | null>(null);
	const [rotaSort, setRotaSort] = useState<ColumnSort | null>(null);

	const handleResizeWidth = (key: string, width: number) => {
		setColWidths((prev) => {
			const next = { ...prev, [key]: width };
			window.localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(next));
			return next;
		});
	};

	useEffect(() => {
		setExpandedKeys(allRotaKeys);
	}, [allRotaKeys]);

	const printExpandedKeys = printOptions.modelo === "rotas-produtores" ? allRotaKeys : [];

	return (
		<ImpressaoContainer className="w-full mt-3 rel-aq-table" title={title} pageStylePrint={PRINT_PAGE_STYLE}>
			<style>{TABLE_STYLE}</style>
			<ImpressaoHeader title={title} />

			<div className="rel-aq-screen">
				<Text type="secondary" className="mb-2 block rel-aq-help">
					Médias de qualidade ponderadas pela litragem do produtor. Clique no título da coluna para ordenar. Arraste
					a borda direita do título para ajustar a largura. Filtro vazio de produtor ou rota inclui todos.
				</Text>

				{resumo && resumo.litros > 0 && resumo.qtdAnalises === 0 ? (
					<Text type="warning" className="mb-2 block rel-aq-warning">
						Há litragem no período, mas nenhuma análise laboratorial (gordura, proteína, CCS, etc.). As médias só
						preenchem se existir análise cadastrada nas mesmas datas — tente o mês completo, por exemplo 06/2026.
					</Text>
				) : null}

				<ResumoCards resumo={resumo} />

				<Tabs
					activeKey={activeTab}
					onChange={onTabChange}
					items={[
						{
							key: "produtores",
							label: "Todos os produtores",
							children: (
								<div className="rel-aq-tab-produtores">
									<TabelaProdutores
										produtores={produtores}
										widths={colWidths}
										onResizeWidth={handleResizeWidth}
										sort={produtorSort}
										onSortChange={setProdutorSort}
									/>
								</div>
							),
						},
						{
							key: "rotas",
							label: "Por rota",
							children: (
								<TabelaRotas
									rotas={rotas}
									expandedKeys={expandedKeys}
									onExpandedRowsChange={setExpandedKeys}
									showProdutores={true}
									widths={colWidths}
									onResizeWidth={handleResizeWidth}
									rotaSort={rotaSort}
									produtorSort={produtorSort}
									onRotaSortChange={setRotaSort}
									onProdutorSortChange={setProdutorSort}
								/>
							),
						},
					]}
				/>
			</div>

			<div className="rel-aq-print">
				{printOptions.incluirResumo ? <ResumoCards resumo={resumo} /> : null}

				{printOptions.modelo === "produtores" ? (
					<TabelaProdutores
						produtores={produtores}
						forPrint={true}
						widths={colWidths}
						onResizeWidth={handleResizeWidth}
						sort={produtorSort}
						onSortChange={setProdutorSort}
					/>
				) : (
					<TabelaRotas
						rotas={rotas}
						expandedKeys={printExpandedKeys}
						showProdutores={printOptions.modelo === "rotas-produtores"}
						forPrint={true}
						widths={colWidths}
						onResizeWidth={handleResizeWidth}
						rotaSort={rotaSort}
						produtorSort={produtorSort}
						onRotaSortChange={setRotaSort}
						onProdutorSortChange={setProdutorSort}
					/>
				)}
			</div>
		</ImpressaoContainer>
	);
}
